import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { authFetch } from "@/lib/authFetch";
import type { RevendeurContrat } from "@shared/schema";

export function useRevendeurContrats(revendeurId: string | undefined) {
  return useQuery<RevendeurContrat[]>({
    queryKey: ["/api/resellers", revendeurId, "contrats"],
    queryFn: async () => {
      if (!revendeurId) return [];
      const res = await authFetch(`/api/resellers/${revendeurId}/contrats`);
      if (!res.ok) throw new Error("Failed to fetch contrats");
      return res.json();
    },
    enabled: !!revendeurId,
  });
}

export function useCreateRevendeurContrat() {
  return useMutation({
    mutationFn: async ({ revendeurId, ...data }: { revendeurId: string; annee: number; label?: string; dateDebut: string; dateFin: string; statut?: string; notes?: string }) => {
      const res = await authFetch(`/api/resellers/${revendeurId}/contrats`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to create contrat");
      return res.json();
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["/api/resellers", vars.revendeurId, "contrats"] });
    },
  });
}

export function useUpdateRevendeurContrat() {
  return useMutation({
    mutationFn: async ({ id, revendeurId, ...data }: { id: string; revendeurId: string; [key: string]: any }) => {
      const res = await authFetch(`/api/reseller-contrats/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to update contrat");
      return res.json();
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["/api/resellers", vars.revendeurId, "contrats"] });
    },
  });
}

export function useDeleteRevendeurContrat() {
  return useMutation({
    mutationFn: async ({ id, revendeurId }: { id: string; revendeurId: string }) => {
      const res = await authFetch(`/api/reseller-contrats/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete contrat");
      return res.json();
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["/api/resellers", vars.revendeurId, "contrats"] });
    },
  });
}
